import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  History, 
  MessageCircle, 
  Mail, 
  Trash2, 
  Settings, 
  Save,
  RefreshCw,
  Phone,
  User,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';

const WHATSAPP_NUMBER = '918983353140';

// Default message templates
const DEFAULT_TEMPLATES = {
  customerFollowUp: `नमस्कार {{customer_name}},

तुमच्या {{loan_type}} अर्जाबद्दल (File: {{file_number}}) आम्ही follow-up करत आहोत.

कृपया pending कागदपत्रे लवकरात लवकर पाठवा.

कागदपत्रे WhatsApp वर पाठवा: https://wa.me/${WHATSAPP_NUMBER}

धन्यवाद!
Atharva Finance Solutions
📞 +91 74100 01036`,

  documentList: `नमस्कार {{customer_name}},

तुमच्या {{loan_type}} साठी खालील कागदपत्रे आवश्यक आहेत:

{{document_list}}

कृपया सर्व कागदपत्रे खालील WhatsApp नंबरवर पाठवा:
📱 https://wa.me/${WHATSAPP_NUMBER}

धन्यवाद!
Atharva Finance Solutions`,

  bankerFollowUp: `नमस्कार {{banker_name}},

Loan File Reminder:
• Customer: {{customer_name}}
• File No: {{file_number}}
• Loan Type: {{loan_type}}
• Amount: {{loan_amount}}

कृपया या फाईलवर action घ्या.

धन्यवाद!
Atharva Finance Solutions`,

  bankerEmail: `Dear {{banker_name}},

A new loan file has been assigned to you for processing.

LOAN DETAILS:
-----------------
File Number: {{file_number}}
Customer Name: {{customer_name}}
Customer Phone: {{customer_phone}}
Loan Type: {{loan_type}}
Loan Amount: {{loan_amount}}

Please review and process the application at your earliest convenience.

For any queries, please contact us at +91 74100 01036.

Best Regards,
Atharva Finance Solutions
info@atharvafs.com`
};

const NotificationHistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState({});
  const [activeTab, setActiveTab] = useState('history');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateContent, setTemplateContent] = useState('');

  useEffect(() => {
    loadHistory();
    loadTemplates();
  }, []);

  const loadHistory = () => {
    const saved = JSON.parse(localStorage.getItem('notificationHistory') || '[]');
    setHistory(saved);
  };

  const loadTemplates = () => {
    const saved = JSON.parse(localStorage.getItem('messageTemplates') || 'null');
    setTemplates(saved || DEFAULT_TEMPLATES);
  };

  const saveTemplates = () => {
    localStorage.setItem('messageTemplates', JSON.stringify(templates));
    toast.success('Templates saved successfully');
  };

  const resetTemplates = () => {
    if (window.confirm('Reset all templates to default?')) {
      setTemplates(DEFAULT_TEMPLATES);
      localStorage.setItem('messageTemplates', JSON.stringify(DEFAULT_TEMPLATES));
      toast.success('Templates reset to default');
    }
  };

  const clearHistory = () => {
    if (window.confirm('Clear all notification history?')) {
      localStorage.removeItem('notificationHistory');
      setHistory([]);
      toast.success('History cleared');
    }
  };

  const handleEditTemplate = (key) => {
    setEditingTemplate(key);
    setTemplateContent(templates[key]);
  };

  const handleSaveTemplate = () => {
    if (editingTemplate) {
      setTemplates(prev => ({
        ...prev,
        [editingTemplate]: templateContent
      }));
      setEditingTemplate(null);
      toast.success('Template updated');
    }
  };

  const getTemplateLabel = (key) => {
    const labels = {
      customerFollowUp: 'Customer Follow-up (WhatsApp)',
      documentList: 'Document List (WhatsApp)',
      bankerFollowUp: 'Banker Follow-up (WhatsApp)',
      bankerEmail: 'Banker Assignment (Email)'
    };
    return labels[key] || key;
  };

  const getMessageTypeBadge = (type) => {
    const styles = {
      'Customer Follow-up': 'bg-green-100 text-green-700',
      'Document List': 'bg-blue-100 text-blue-700',
      'Banker Follow-up': 'bg-violet-100 text-violet-700',
      'Banker Email': 'bg-amber-100 text-amber-700'
    };
    return styles[type] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div data-testid="notification-history-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Notification Center</h1>
          <p className="text-slate-500 mt-1">Message history and templates</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History ({history.length})
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Templates
          </TabsTrigger>
        </TabsList>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading text-brand-navy">
                  Sent Notifications
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadHistory}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearHistory}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-600">Type</TableHead>
                    <TableHead className="font-semibold text-slate-600">Message Type</TableHead>
                    <TableHead className="font-semibold text-slate-600">Recipient</TableHead>
                    <TableHead className="font-semibold text-slate-600">Phone</TableHead>
                    <TableHead className="font-semibold text-slate-600">Loan File</TableHead>
                    <TableHead className="font-semibold text-slate-600">Sent At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id} className="hover:bg-slate-50/50">
                      <TableCell>
                        {item.type === 'whatsapp' ? (
                          <Badge className="bg-green-100 text-green-700">
                            <MessageCircle className="h-3 w-3 mr-1" />
                            WhatsApp
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700">
                            <Mail className="h-3 w-3 mr-1" />
                            Email
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getMessageTypeBadge(item.message_type)}>
                          {item.message_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-slate-400" />
                          {item.recipient}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-slate-400" />
                          {item.phone || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <FileText className="h-3 w-3 text-slate-400" />
                          {item.loan_file}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {format(new Date(item.sent_at), 'dd MMM yyyy, hh:mm a')}
                      </TableCell>
                    </TableRow>
                  ))}
                  {history.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        <History className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        No notifications sent yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading text-brand-navy">
                  Message Templates
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={resetTemplates}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Reset to Default
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={saveTemplates}
                    className="bg-brand-navy text-white hover:bg-brand-navy/90"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Save All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Available Variables:</strong> {`{{customer_name}}, {{loan_type}}, {{file_number}}, {{loan_amount}}, {{customer_phone}}, {{banker_name}}, {{document_list}}`}
                  </p>
                </div>
                
                {Object.keys(templates).map((key) => (
                  <Card key={key} className="border border-slate-200">
                    <CardHeader className="py-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium text-brand-navy">
                          {getTemplateLabel(key)}
                        </CardTitle>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditTemplate(key)}
                        >
                          Edit
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="py-2">
                      <pre className="text-xs text-slate-600 whitespace-pre-wrap bg-slate-50 p-3 rounded max-h-32 overflow-y-auto">
                        {templates[key]}
                      </pre>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Template Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">
              Edit Template: {editingTemplate && getTemplateLabel(editingTemplate)}
            </DialogTitle>
            <DialogDescription>
              Use variables like {`{{customer_name}}`} for dynamic content
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={templateContent}
              onChange={(e) => setTemplateContent(e.target.value)}
              className="min-h-[300px] font-mono text-sm"
              placeholder="Enter message template..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTemplate}
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
            >
              <Save className="h-4 w-4 mr-1" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationHistoryPage;
